/**
 * Router — declaración central de TODAS las rutas (CONTRACTS.md §1).
 *
 * Estructura por layout anidado:
 *  /                         → MainLayout (Navbar + Footer + WhatsApp flotante)
 *  /auth/*                   → AuthLayout (full-screen, sin navbar/footer)
 *  /cuenta/*                 → CuentaLayout (sidebar cliente, RequireAuth)
 *  /admin/*                  → AdminLayout (sidebar admin, RequireAdmin)
 *
 * F9 — Code-splitting:
 *  - `Home` se importa eager (LCP crítico, primera ruta del usuario).
 *  - El resto se carga con `React.lazy` para que Rollup las separe en
 *    chunks por ruta. Suspense renderiza un Skeleton mientras carga.
 *  - Layouts se mantienen eager (siempre los necesita el shell).
 *  - Vendors se separan vía `build.rollupOptions.output.manualChunks` en
 *    vite.config.ts (react, supabase, sentry, motion, query).
 *
 * Las exportaciones de las pages son NAMED, no default — el helper
 * `lazyNamed` adapta `import('...').then(m => m.X)` a la firma que
 * `React.lazy` espera.
 */
import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

import { MainLayout } from './layouts/MainLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { CuentaLayout } from './layouts/CuentaLayout';
import { AdminLayout } from './layouts/AdminLayout';
import { RequireAuth, RequireAdmin } from './require-auth';
import { Skeleton } from '../components/ui/Skeleton';

// LCP route — eager para evitar un Suspense flash en el primer paint.
import { Home } from '../pages/Home';

// ---------------------------------------------------------------------------
// Helper para usar React.lazy con NAMED exports.
// ---------------------------------------------------------------------------
function lazyNamed<P extends Record<string, unknown>, K extends keyof P>(
  factory: () => Promise<P>,
  name: K,
): ReturnType<typeof lazy> {
  return lazy(() =>
    factory().then((mod) => ({
      default: mod[name] as unknown as ComponentType<unknown>,
    })),
  );
}

// ---------------------------------------------------------------------------
// Public pages — lazy (cada una su chunk).
// ---------------------------------------------------------------------------
const Terapias = lazyNamed(() => import('../pages/Terapias'), 'Terapias');
const TerapiaDetalle = lazyNamed(() => import('../pages/TerapiaDetalle'), 'TerapiaDetalle');
const Tienda = lazyNamed(() => import('../pages/Tienda'), 'Tienda');
const ProductoDetalle = lazyNamed(() => import('../pages/ProductoDetalle'), 'ProductoDetalle');
const TiendaEncargo = lazyNamed(() => import('../pages/TiendaEncargo'), 'TiendaEncargo');
const Galeria = lazyNamed(() => import('../pages/Galeria'), 'Galeria');
const SobreMi = lazyNamed(() => import('../pages/SobreMi'), 'SobreMi');
const EventosLista = lazyNamed(() => import('../pages/EventosLista'), 'EventosLista');
const EventoDetalle = lazyNamed(() => import('../pages/EventoDetalle'), 'EventoDetalle');
const Contacto = lazyNamed(() => import('../pages/Contacto'), 'Contacto');
const Recursos = lazyNamed(() => import('../pages/Recursos'), 'Recursos');
// Subpáginas placeholder de Recursos — cada una usa el componente
// `_RecursoComingSoon` compartido y se mantienen como rutas separadas para
// que el admin pueda alimentar su contenido editorial de forma independiente.
const RecursoGuiaTerapia = lazyNamed(() => import('../pages/recursos/GuiaTerapia'), 'GuiaTerapia');
const RecursoPreguntasFrecuentes = lazyNamed(
  () => import('../pages/recursos/PreguntasFrecuentes'),
  'PreguntasFrecuentes',
);
const RecursoPrepararSesion = lazyNamed(
  () => import('../pages/recursos/PrepararSesion'),
  'PrepararSesion',
);
const RecursoCuidadoEnergetico = lazyNamed(
  () => import('../pages/recursos/CuidadoEnergetico'),
  'CuidadoEnergetico',
);
const RecursoArteSanacion = lazyNamed(
  () => import('../pages/recursos/ArteSanacion'),
  'ArteSanacion',
);
const RecursoInspiracionAlma = lazyNamed(
  () => import('../pages/recursos/InspiracionAlma'),
  'InspiracionAlma',
);
const PagoResultado = lazyNamed(() => import('../pages/PagoResultado'), 'PagoResultado');
const NotFound = lazyNamed(() => import('../pages/NotFound'), 'NotFound');

// Auth bundle
const Login = lazyNamed(() => import('../pages/auth/Login'), 'Login');
const Register = lazyNamed(() => import('../pages/auth/Register'), 'Register');
const Recover = lazyNamed(() => import('../pages/auth/Recover'), 'Recover');
const Reset = lazyNamed(() => import('../pages/auth/Reset'), 'Reset');

// Cuenta bundle
const Cuenta = lazyNamed(() => import('../pages/cuenta/Cuenta'), 'Cuenta');
const Citas = lazyNamed(() => import('../pages/cuenta/Citas'), 'Citas');
const Pedidos = lazyNamed(() => import('../pages/cuenta/Pedidos'), 'Pedidos');
const Favoritos = lazyNamed(() => import('../pages/cuenta/Favoritos'), 'Favoritos');
const Mensajes = lazyNamed(() => import('../pages/cuenta/Mensajes'), 'Mensajes');
const CuentaConfiguracion = lazyNamed(
  () => import('../pages/cuenta/Configuracion'),
  'Configuracion',
);
const Carrito = lazyNamed(() => import('../pages/cuenta/Carrito'), 'Carrito');

// Admin bundle
const Dashboard = lazyNamed(() => import('../pages/admin/Dashboard'), 'Dashboard');
const Agendamiento = lazyNamed(() => import('../pages/admin/Agendamiento'), 'Agendamiento');
const Correos = lazyNamed(() => import('../pages/admin/Correos'), 'Correos');
const AdminGaleria = lazyNamed(() => import('../pages/admin/Galeria'), 'Galeria');
const AdminEventos = lazyNamed(() => import('../pages/admin/Eventos'), 'Eventos');
const Audios = lazyNamed(() => import('../pages/admin/Audios'), 'Audios');
const AdminContenido = lazyNamed(() => import('../pages/admin/Contenido'), 'Contenido');
const AdminConfiguracion = lazyNamed(() => import('../pages/admin/Configuracion'), 'Configuracion');
const Catalogos = lazyNamed(() => import('../pages/admin/Catalogos'), 'Catalogos');
const AdminRecursos = lazyNamed(() => import('../pages/admin/Recursos'), 'Recursos');
const Usuarios = lazyNamed(() => import('../pages/admin/Usuarios'), 'Usuarios');
const Reportes = lazyNamed(() => import('../pages/admin/Reportes'), 'Reportes');

// Admin — editores dedicados (refactor backoffice: reemplazan modales).
const RecursoEditor = lazyNamed(() => import('../pages/admin/RecursoEditor'), 'RecursoEditor');
const PlantillaCorreoEditor = lazyNamed(
  () => import('../pages/admin/PlantillaCorreoEditor'),
  'PlantillaCorreoEditor',
);
const GaleriaItemEditor = lazyNamed(
  () => import('../pages/admin/GaleriaItemEditor'),
  'GaleriaItemEditor',
);
const EventoEditor = lazyNamed(() => import('../pages/admin/EventoEditor'), 'EventoEditor');
// Admin > Catálogos — editores dedicados (Turno 2).
const TerapiaEditor = lazyNamed(() => import('../pages/admin/TerapiaEditor'), 'TerapiaEditor');
const ProductoEditor = lazyNamed(() => import('../pages/admin/ProductoEditor'), 'ProductoEditor');
const TestimonioEditor = lazyNamed(
  () => import('../pages/admin/TestimonioEditor'),
  'TestimonioEditor',
);
const AdminTestimonios = lazyNamed(() => import('../pages/admin/Testimonios'), 'AdminTestimonios');

// Legal
const Privacidad = lazyNamed(() => import('../pages/legal/Privacidad'), 'Privacidad');
const Terminos = lazyNamed(() => import('../pages/legal/Terminos'), 'Terminos');
const Cookies = lazyNamed(() => import('../pages/legal/Cookies'), 'Cookies');

// ---------------------------------------------------------------------------
// Fallback para Suspense. Sutil — emula el shape de una pageHeader + grid
// de 6 cards, suficiente para mantener layout estable sin parpadeo.
// ---------------------------------------------------------------------------
function RouteFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Cargando contenido"
      className="container mx-auto px-4 py-12 space-y-8"
    >
      <div className="space-y-3 max-w-xl">
        <Skeleton height={20} width="40%" rounded="md" />
        <Skeleton height={36} width="80%" rounded="md" />
        <Skeleton lines={2} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} height={200} rounded="md" />
        ))}
      </div>
    </div>
  );
}

/** Wrapper local: cada elemento del router lazy va envuelto en Suspense. */
function S({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <MainLayout />,
    children: [
      { index: true, element: <Home /> },
      {
        path: 'terapias',
        element: (
          <S>
            <Terapias />
          </S>
        ),
      },
      {
        path: 'terapias/:slug',
        element: (
          <S>
            <TerapiaDetalle />
          </S>
        ),
      },
      {
        path: 'tienda',
        element: (
          <S>
            <Tienda />
          </S>
        ),
      },
      {
        path: 'tienda/encargo',
        element: (
          <S>
            <TiendaEncargo />
          </S>
        ),
      },
      {
        path: 'tienda/:slug',
        element: (
          <S>
            <ProductoDetalle />
          </S>
        ),
      },
      {
        path: 'galeria',
        element: (
          <S>
            <Galeria />
          </S>
        ),
      },
      {
        path: 'sobre-mi',
        element: (
          <S>
            <SobreMi />
          </S>
        ),
      },
      {
        path: 'eventos',
        element: (
          <S>
            <EventosLista />
          </S>
        ),
      },
      {
        path: 'eventos/:slug',
        element: (
          <S>
            <EventoDetalle />
          </S>
        ),
      },
      {
        path: 'recursos',
        element: (
          <S>
            <Recursos />
          </S>
        ),
      },
      {
        path: 'recursos/guia-terapia',
        element: (
          <S>
            <RecursoGuiaTerapia />
          </S>
        ),
      },
      {
        path: 'recursos/preguntas-frecuentes',
        element: (
          <S>
            <RecursoPreguntasFrecuentes />
          </S>
        ),
      },
      {
        path: 'recursos/preparate',
        element: (
          <S>
            <RecursoPrepararSesion />
          </S>
        ),
      },
      {
        path: 'recursos/cuidado-energetico',
        element: (
          <S>
            <RecursoCuidadoEnergetico />
          </S>
        ),
      },
      {
        path: 'recursos/arte-sanacion',
        element: (
          <S>
            <RecursoArteSanacion />
          </S>
        ),
      },
      {
        path: 'recursos/inspiracion-alma',
        element: (
          <S>
            <RecursoInspiracionAlma />
          </S>
        ),
      },
      {
        path: 'contacto',
        element: (
          <S>
            <Contacto />
          </S>
        ),
      },
      {
        path: 'pago/resultado',
        element: (
          <S>
            <PagoResultado />
          </S>
        ),
      },
      {
        path: 'legal/privacidad',
        element: (
          <S>
            <Privacidad />
          </S>
        ),
      },
      {
        path: 'legal/terminos',
        element: (
          <S>
            <Terminos />
          </S>
        ),
      },
      {
        path: 'legal/cookies',
        element: (
          <S>
            <Cookies />
          </S>
        ),
      },
      // Alias retro-compat: /configuracion → /admin (legacy del repo original)
      { path: 'configuracion', element: <Navigate to="/admin" replace /> },
    ],
  },
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      { index: true, element: <Navigate to="/auth/login" replace /> },
      {
        path: 'login',
        element: (
          <S>
            <Login />
          </S>
        ),
      },
      {
        path: 'register',
        element: (
          <S>
            <Register />
          </S>
        ),
      },
      {
        path: 'recover',
        element: (
          <S>
            <Recover />
          </S>
        ),
      },
      {
        path: 'reset',
        element: (
          <S>
            <Reset />
          </S>
        ),
      },
    ],
  },
  {
    path: '/cuenta',
    element: (
      <RequireAuth>
        <CuentaLayout />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: (
          <S>
            <Cuenta />
          </S>
        ),
      },
      {
        path: 'citas',
        element: (
          <S>
            <Citas />
          </S>
        ),
      },
      {
        path: 'pedidos',
        element: (
          <S>
            <Pedidos />
          </S>
        ),
      },
      {
        path: 'favoritos',
        element: (
          <S>
            <Favoritos />
          </S>
        ),
      },
      {
        path: 'mensajes',
        element: (
          <S>
            <Mensajes />
          </S>
        ),
      },
      {
        path: 'configuracion',
        element: (
          <S>
            <CuentaConfiguracion />
          </S>
        ),
      },
      {
        path: 'carrito',
        element: (
          <S>
            <Carrito />
          </S>
        ),
      },
    ],
  },
  {
    path: '/admin',
    element: (
      <RequireAdmin>
        <AdminLayout />
      </RequireAdmin>
    ),
    children: [
      {
        index: true,
        element: (
          <S>
            <Dashboard />
          </S>
        ),
      },
      {
        path: 'agendamiento',
        element: (
          <S>
            <Agendamiento />
          </S>
        ),
      },
      {
        path: 'correos',
        element: (
          <S>
            <Correos />
          </S>
        ),
      },
      {
        path: 'correos/plantillas/:slug/editar',
        element: (
          <S>
            <PlantillaCorreoEditor />
          </S>
        ),
      },
      {
        path: 'galeria',
        element: (
          <S>
            <AdminGaleria />
          </S>
        ),
      },
      {
        path: 'galeria/nueva',
        element: (
          <S>
            <GaleriaItemEditor />
          </S>
        ),
      },
      {
        path: 'galeria/:id/editar',
        element: (
          <S>
            <GaleriaItemEditor />
          </S>
        ),
      },
      {
        path: 'eventos',
        element: (
          <S>
            <AdminEventos />
          </S>
        ),
      },
      {
        path: 'eventos/nuevo',
        element: (
          <S>
            <EventoEditor />
          </S>
        ),
      },
      {
        path: 'eventos/:id/editar',
        element: (
          <S>
            <EventoEditor />
          </S>
        ),
      },
      {
        path: 'audios',
        element: (
          <S>
            <Audios />
          </S>
        ),
      },
      {
        path: 'contenido',
        element: (
          <S>
            <AdminContenido />
          </S>
        ),
      },
      {
        path: 'configuracion',
        element: (
          <S>
            <AdminConfiguracion />
          </S>
        ),
      },
      {
        path: 'catalogos',
        element: (
          <S>
            <Catalogos />
          </S>
        ),
      },
      {
        path: 'catalogos/terapias/nueva',
        element: (
          <S>
            <TerapiaEditor />
          </S>
        ),
      },
      {
        path: 'catalogos/terapias/:id/editar',
        element: (
          <S>
            <TerapiaEditor />
          </S>
        ),
      },
      {
        path: 'catalogos/productos/nuevo',
        element: (
          <S>
            <ProductoEditor />
          </S>
        ),
      },
      {
        path: 'catalogos/productos/:id/editar',
        element: (
          <S>
            <ProductoEditor />
          </S>
        ),
      },
      {
        path: 'catalogos/testimonios/nuevo',
        element: (
          <S>
            <TestimonioEditor />
          </S>
        ),
      },
      {
        path: 'catalogos/testimonios/:id/editar',
        element: (
          <S>
            <TestimonioEditor />
          </S>
        ),
      },
      {
        path: 'testimonios',
        element: (
          <S>
            <AdminTestimonios />
          </S>
        ),
      },
      {
        path: 'recursos',
        element: (
          <S>
            <AdminRecursos />
          </S>
        ),
      },
      {
        path: 'recursos/nuevo',
        element: (
          <S>
            <RecursoEditor />
          </S>
        ),
      },
      {
        path: 'recursos/:id/editar',
        element: (
          <S>
            <RecursoEditor />
          </S>
        ),
      },
      {
        path: 'usuarios',
        element: (
          <S>
            <Usuarios />
          </S>
        ),
      },
      {
        path: 'reportes',
        element: (
          <S>
            <Reportes />
          </S>
        ),
      },
    ],
  },
  // Catch-all 404 (fuera de layouts para no forzar Navbar en errores 404 profundos)
  {
    path: '*',
    element: <MainLayout />,
    children: [
      {
        path: '*',
        element: (
          <S>
            <NotFound />
          </S>
        ),
      },
    ],
  },
]);

/** Wrapper para usar en App.tsx. */
export function AppRouter() {
  return <RouterProvider router={router} />;
}
